/**
 * MagicLinkLogin — passwordless email-code sign-in for external Data
 * Room collaborators (Outlook/O365/corporate emails where Google Auth
 * isn't guaranteed to work).
 *
 * Two-step UX:
 *   1) Enter email → POST /api/auth/magic-link/request  (always succeeds
 *      with a generic message so we don't enumerate collaborators).
 *   2) Enter the 6-digit code → POST /api/auth/magic-link/verify → we
 *      seed AuthContext with the returned user and hand off to onSuccess
 *      (typically a redirect to the requested `next` route).
 *
 * The component is self-contained and reusable — dropped into
 * LoginPage.js and DataRoomWelcomePage.js.
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, KeyRound, Loader2, ArrowLeft, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MagicLinkLogin({ defaultEmail = '', onSuccess, testIdPrefix = 'magic-link' }) {
  const [step, setStep] = useState('email');       // 'email' | 'code'
  const [email, setEmail] = useState(defaultEmail);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);      // seconds until "Resend code" enables
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (step === 'code' && codeInputRef.current) codeInputRef.current.focus();
  }, [step]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const requestCode = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/magic-link/request`, { email: trimmed });
      toast.success(`Code sent to ${trimmed}. Check your inbox.`);
      setEmail(trimmed);
      setStep('code');
      setCooldown(30);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send code. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    const c = code.trim();
    if (!/^\d{6}$/.test(c)) {
      toast.error('Enter the 6-digit code from your email');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/auth/magic-link/verify`,
        { email: email.trim().toLowerCase(), code: c },
        { withCredentials: true },
      );
      // Stash the session_token so multipart uploads work through
      // Cloudflare (same pattern as AuthContext + /auth/me).
      if (res.data?.session_token && typeof window !== 'undefined') {
        window.__FR_SESSION_TOKEN__ = res.data.session_token;
      }
      toast.success(`Welcome, ${res.data?.name || res.data?.email}`);
      if (onSuccess) onSuccess(res.data);
      else window.location.href = '/dashboard';
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Verification failed';
      toast.error(detail);
      // On "invalid or expired" — bump user back to email step.
      if (err?.response?.status === 401 && /expired|new one/i.test(detail)) {
        setCode('');
        setStep('email');
      }
    } finally {
      setBusy(false);
    }
  };

  const onCodeKeyDown = (e) => {
    if (e.key === 'Enter') verifyCode();
  };

  if (step === 'email') {
    return (
      <div className="space-y-3" data-testid={`${testIdPrefix}-email-step`}>
        <div className="relative">
          <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && requestCode()}
            placeholder="you@company.com"
            autoComplete="email"
            className="w-full h-11 pl-10 pr-3 text-sm rounded-lg border border-slate-200 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            data-testid={`${testIdPrefix}-email-input`}
          />
        </div>
        <button
          type="button"
          onClick={requestCode}
          disabled={busy || !email.trim()}
          className="w-full h-11 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid={`${testIdPrefix}-request-btn`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
          {busy ? 'Sending code…' : 'Email me a sign-in code'}
        </button>
        <p className="text-[11px] text-slate-500 text-center">
          We&apos;ll send a one-time 6-digit code. No password needed.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-code-step`}>
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[12px] text-slate-600 flex items-center justify-between">
        <span>Code sent to <span className="font-semibold text-slate-800">{email}</span></span>
        <button
          type="button"
          onClick={() => { setStep('email'); setCode(''); }}
          className="text-[11px] text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
          data-testid={`${testIdPrefix}-change-email`}
        >
          <ArrowLeft className="w-3 h-3" /> Change
        </button>
      </div>
      <input
        ref={codeInputRef}
        type="text"
        inputMode="numeric"
        pattern="\d{6}"
        maxLength={6}
        value={code}
        onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        onKeyDown={onCodeKeyDown}
        placeholder="6-digit code"
        autoComplete="one-time-code"
        className="w-full h-14 text-center text-2xl font-mono tracking-[0.6em] rounded-lg border border-slate-200 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
        data-testid={`${testIdPrefix}-code-input`}
      />
      <button
        type="button"
        onClick={verifyCode}
        disabled={busy || code.length !== 6}
        className="w-full h-11 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid={`${testIdPrefix}-verify-btn`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        {busy ? 'Verifying…' : 'Verify & sign in'}
      </button>
      <div className="flex items-center justify-between text-[11px]">
        <button
          type="button"
          onClick={requestCode}
          disabled={busy || cooldown > 0}
          className="text-indigo-600 hover:text-indigo-800 disabled:text-slate-400"
          data-testid={`${testIdPrefix}-resend-btn`}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
        </button>
        <span className="text-slate-400">Code expires in 10 minutes</span>
      </div>
    </div>
  );
}
